/** The Tax workspace groups each Magic Insights question in one place. */
export type TaxMagicCategory =
  | 'company'
  | 'scale'
  | 'activity'
  | 'accounts'
  | 'innovation'
  | 'property'
  | 'energy'
  | 'international'
  | 'workspace';

export interface TaxMagicRelevanceFilter {
  fieldName?: string;
  taxDisplayLabel?: string;
  label?: string;
}

/**
 * The shared Tax template has stable field names, although its question IDs
 * can differ between environments. These are its visible search fields.
 */
const SHARED_TAX_FIELDS: Readonly<Record<string, TaxMagicCategory>> = {
  strategic_goals__strategic_goal: 'activity',
  tax_arrangement_summary: 'accounts',
  auditors__auditor_firm_name: 'accounts',
  accountants__accountancy_firm_name: 'accounts',
  tax_advisors__tax_advisory_firm_name: 'accounts',
  key_risks__key_risk: 'activity',
  challenges__challenge: 'activity',
  r_and_d_tax_relief_future_claim_amount: 'innovation',
  r_and_d_tax_relief_likelihood_of_future_claims: 'innovation',
  capital_allowances_tax_relief_future_claim_amount: 'property',
  capital_allowances_tax_relief_likelihood_of_future_claims: 'property',
  land_remediation_tax_relief_future_claim_amount: 'property',
  land_remediation_tax_relief_likelihood_of_future_claims: 'property',
  eii_ccl_scheme_future_claim_amount: 'energy',
  eii_ccl_scheme_likelihood_of_future_claims: 'energy',
  patent_box_tax_relief_future_claim_amount: 'innovation',
  patent_box_tax_relief_likelihood_of_future_claims: 'innovation',
  innovate_uk_grant_funding_future_application_amount: 'innovation',
  innovate_uk_grant_funding_likelihood_of_future_applications: 'innovation',
  creative_industries_tax_relief_future_claim_amount: 'innovation',
  creative_industries_tax_relief_likelihood_of_future_claims: 'innovation',
};

/**
 * Route each public Tax question once, using the shared template's stable
 * field identity. New fields stay visible in Your workspace until reviewed.
 */
export function taxMagicCategory(
  moduleName: string,
  _questionId: number,
  filters: ReadonlyArray<TaxMagicRelevanceFilter>,
): TaxMagicCategory {
  if (moduleName !== 'Magic Insights - Tax') return 'workspace';
  const categories = new Set(filters.map(filter => SHARED_TAX_FIELDS[filter.fieldName || ''])
    .filter((category): category is TaxMagicCategory => Boolean(category)));
  return categories.size === 1 ? [...categories][0] : 'workspace';
}
